// @ts-nocheck
import React, { useEffect, useState, useRef } from 'react';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';
import swaggerSpec from './swagger.json';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { executeRequest } from './ApiProxy';

// Wrapper component to handle SwaggerUI without Strict Mode warnings
const SwaggerUIWrapper = (props) => {
  // This is a regular component that doesn't use Strict Mode
  return <SwaggerUI {...props} />;
};

interface UserState {
  status: string;
  details: {
    groups?: string[];
    email?: string;
    username?: string;
  };
  permissionDetails: {
    id?: string;
    name?: string;
    email?: string;
    profilePictureUrl?: string;
  };
  token?: string;
  organizations?: {
    loaded: boolean;
    list: any[];
  };
}

const ApiDocumentation: React.FC = () => {
  const [spec, setSpec] = useState(swaggerSpec);
  const user = useSelector((state: RootState) => state.user) as UserState;
  const [authToken, setAuthToken] = useState<string | null>(localStorage.getItem('swaggerApiToken'));
  const swaggerUIRef = useRef<any>(null);
  const isAdmin = user?.details?.groups?.includes('Admin');

  // Update the Swagger spec with the API base URL
  useEffect(() => {
    // Simplified token fetching - using Amplify's fetchAuthSession
    const fetchToken = async () => {
      try {
        const { fetchAuthSession } = await import('aws-amplify/auth');
        const session = await fetchAuthSession();
        if (session?.tokens?.idToken) {
          const token = session.tokens.idToken.toString();
          setAuthToken(token);
          localStorage.setItem('swaggerApiToken', token);
          console.log('Authentication token retrieved for API calls');
        }
      } catch (err) {
        console.error('Error fetching auth token:', err);
      }
    };
    
    // Modify the swagger spec based on user permissions
    if (!isAdmin) {
      // Create a modified spec with only the production server
      const modifiedSpec = {
        ...spec,
        servers: [
          {
            url: "https://2lq79cogme.execute-api.us-east-1.amazonaws.com/prod",
            description: "Production Environment"
          }
        ]
      };
      setSpec(modifiedSpec);
    }
    
    fetchToken();
    
    // Set up interval to refresh token periodically
    const tokenRefreshInterval = setInterval(fetchToken, 15 * 60 * 1000); // Refresh every 15 minutes
    
    return () => {
      clearInterval(tokenRefreshInterval);
    };
  }, [isAdmin]);

  // Plugin to automatically set Authorization header for all requests
  const AuthorizationPlugin = () => {
    return {
      statePlugins: {
        auth: {
          wrapActions: {
            authorize: (oriAction: any) => (payload: any) => {
              return oriAction(payload);
            }
          }
        },
        spec: {
          wrapActions: {
            // After a response is received from the login endpoint
            responseReceived: (oriAction: any) => (action: any) => {
              const result = oriAction(action);
              
              // Check if this is a login response
              if (action.payload && action.payload.url && action.payload.url.includes('/api/login')) {
                try {
                  // Extract token from response
                  const response = action.payload;
                  const responseJson = typeof response.data === 'string' 
                    ? JSON.parse(response.data) 
                    : response.data;
                    
                  // If we find a token in the response (try both possible locations)
                  const token = responseJson.token || (responseJson.body && responseJson.body.token);
                  
                  if (token) {
                    console.log('Login successful, token received');
                    
                    // Store the token
                    localStorage.setItem('swaggerApiToken', token);
                    
                    // Get the Swagger UI instance
                    if (swaggerUIRef.current && swaggerUIRef.current.getSystem) {
                      const system = swaggerUIRef.current.getSystem();
                      
                      // Set the auth token in Swagger UI
                      system.authActions.authorize({
                        "bearerAuth": {
                          name: "bearerAuth",
                          schema: {
                            type: "http",
                            scheme: "bearer",
                            bearerFormat: "JWT"
                          },
                          value: token
                        }
                      });
                      
                      console.log('Auth token automatically applied to Swagger UI');
                    }
                  }
                } catch (err) {
                  console.error('Error processing login response', err);
                }
              }
              
              return result;
            }
          }
        }
      }
    };
  };

  // Function to clear the JWT token
  const clearAuthToken = () => {
    localStorage.removeItem('swaggerApiToken');
    setAuthToken(null);
    
    // Clear token from Swagger UI if reference is available
    if (swaggerUIRef.current && swaggerUIRef.current.getSystem) {
      const system = swaggerUIRef.current.getSystem();
      system.authActions.logout(['bearerAuth']);
      console.log('Auth token cleared');
    }
    
    alert('JWT token has been cleared');
  };

  return (
    <div className="p-4 bg-white min-h-full w-full">
      <div className="w-full">
        
        <div className="bg-blue-100 border-l-4 border-blue-500 text-blue-700 p-4 mb-6" role="alert">
          <p className="font-bold">Interactive API Testing:</p>
          <p>
            Use the "Try it out" button on each endpoint to make real API calls. For protected endpoints, you'll need to:
          </p>
          <ol className="list-decimal ml-8 mt-2">
            <li>Use the <code>/api/login</code> endpoint to get a token</li>
            <li>The token will be automatically applied to all subsequent requests</li>
            <li>
              <button 
                onClick={clearAuthToken}
                className="ml-2 px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                Clear JWT Token
              </button>
            </li>
          </ol>
        </div>
        
        <div className="mb-6 bg-gray-100 p-4 rounded-lg">
          <h2 className="text-xl mb-3 font-semibold">Example API Usage</h2>
          
          <div className="mb-4">
            <h3 className="text-lg font-medium mb-2">Authentication Example (cURL)</h3>
            <pre className="bg-gray-900 text-gray-100 p-3 rounded overflow-x-auto">
              {`curl -X POST https://2lq79cogme.execute-api.us-east-1.amazonaws.com/prod/api/login \\
  -H "Content-Type: application/json" \\
  -d '{"email": "your-email@example.com", "password": "your-password"}'`}
            </pre>
          </div>
          
          <div className="mb-4">
            <h3 className="text-lg font-medium mb-2">Quick Guide to Swagger UI Authentication</h3>
            <div className="bg-white p-4 rounded border border-blue-200">
              <ol className="list-decimal ml-6 space-y-2">
                <li>Click the <strong>Authorize</strong> button at the top right of the documentation</li>
                <li>Enter your JWT token in the input field (available after login)</li>
                <li>Click <strong>Authorize</strong> and then <strong>Close</strong></li>
                <li>All your API requests will now include the authorization header</li>
              </ol>
              <div className="mt-4 text-sm text-gray-600">
                <p><strong>Note:</strong> After using the login endpoint, your token will be automatically applied.</p>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow w-full">
          <SwaggerUIWrapper
            className="w-full" 
            onComplete={(system) => {
              // Store the system object when the component is mounted
              swaggerUIRef.current = { getSystem: () => system };
            }}
            spec={spec} 
            docExpansion="list"
            plugins={[AuthorizationPlugin]}
            supportedSubmitMethods={["get", "post", "put", "delete"]}
            requestInterceptor={(req) => {
              // Add token to request if available
              const token = localStorage.getItem('swaggerApiToken');
              if (token && req.headers) {
                req.headers.Authorization = `Bearer ${token}`;
              }
              return req;
            }}
            requestManager={{
              execute: executeRequest
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ApiDocumentation;